import React from "react";
import { Card, Typography, Avatar, Space, Input } from "antd";
import {
    EllipsisOutlined,
    HeartOutlined,
    MessageOutlined,
    SendOutlined,
    SaveOutlined,
    LeftOutlined,
    RightOutlined,
    SoundOutlined,
    SoundFilled,
} from "@ant-design/icons";
import TextRenderer from "../../utils/TextUtils/textRendererForLivePreview";
import { useTranslation } from "react-i18next";
import useIsWideImage from "../../utils/ImageUtils/useIsWideImage";

export default function InstagramPreview({
                                             text,
                                             images = [],
                                             videoUrl = null,
                                             profileName,
                                             videoAutoplay = false,
                                             videoMuted = false,
                                             videoLoop = true,
                                             mode = "post",
                                             storyAutoAdvance = false,
                                             storyAdvanceMs = 5000,
                                             storyMuted,
                                         }) {
    const [index, setIndex] = React.useState(0);
    const [expanded, setExpanded] = React.useState(false);
    const [isStoryMuted, setIsStoryMuted] = React.useState(
        storyMuted ?? true
    );

    const clamp = 440;
    const uname = (profileName || "account").toString();
    const initials = uname?.[0]?.toUpperCase() || "A";
    const { t } = useTranslation();

    const media = React.useMemo(() => {
        const imgs = (images || []).map((it) => ({ type: "image", url: it?.url }));
        const vid = videoUrl ? [{ type: "video", url: videoUrl }] : [];
        return [...imgs, ...vid].filter((m) => !!m.url);
    }, [images, videoUrl]);

    const hasMultiple = media.length > 1;
    const current = media[index];
    const isWide = useIsWideImage(
        mode === "post" && current?.type === "image" ? current?.url : null
    );

    const onPrev = (e) => {
        e?.stopPropagation?.();
        setIndex((i) => (i - 1 + media.length) % media.length);
    };
    const onNext = (e) => {
        e?.stopPropagation?.();
        setIndex((i) => (i + 1) % media.length);
    };

    React.useEffect(() => {
        if (mode !== "story" || !storyAutoAdvance || media.length <= 1) return;
        const timer = setTimeout(() => {
            setIndex((i) => (i + 1) % media.length);
        }, storyAdvanceMs);
        return () => clearTimeout(timer);
    }, [mode, storyAutoAdvance, storyAdvanceMs, media.length, index]);

    const renderHeader = () => (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: mode === "story" ? 8 : 12,
            }}
        >
            <Space align="center">
                {/* Story ring when in story mode */}
                <div
                    style={{
                        padding: mode === "story" ? 2 : 0,
                        borderRadius: "50%",
                        background:
                            mode === "story"
                                ? "conic-gradient(#e1306c, #f56040, #fbad50, #e1306c)"
                                : "transparent",
                    }}
                >
                    <Avatar size={32} style={{ background: "#d9d9d9" }}>
                        {initials}
                    </Avatar>
                </div>
                <Typography.Text strong>{uname}</Typography.Text>
            </Space>
            <EllipsisOutlined style={{ fontSize: 18, color: "#595959" }} />
        </div>
    );

    const renderMedia = () => {
        const commonVideoProps = {
            key: current?.url,
            src: current?.url,
            style: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
            },
            playsInline: true,
            loop: videoLoop,
            preload: "metadata",
            controls: mode === "post",
        };

        if (current?.type === "image") {
            return (
                <img
                    src={current.url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: mode === "post" ? (isWide ? "contain" : "cover") : "cover",
                        background: "#000",
                        backfaceVisibility: "hidden",
                        willChange: "transform",
                    }}
                />
            );
        }

        if (current?.type === "video") {
            if (mode === "story") {
                return (
                    <video
                        {...commonVideoProps}
                        autoPlay
                        muted={isStoryMuted}
                        onClick={() => setIsStoryMuted((m) => !m)}
                    />
                );
            }
            return (
                <video
                    {...commonVideoProps}
                    autoPlay={videoAutoplay}
                    muted={videoMuted}
                />
            );
        }

        return <div style={{ position: "absolute", inset: 0, background: "#f0f0f0" }} />;
    };

    const renderPostChrome = () => (
        <>
            {hasMultiple && (
                <div
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        borderRadius: 16,
                        padding: "2px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    {index + 1}/{media.length}
                </div>
            )}

            {/* arrows */}
            {hasMultiple && (
                <>
                    <button
                        type="button"
                        onClick={onPrev}
                        style={{
                            position: "absolute",
                            left: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            border: "none",
                            background: "rgba(0,0,0,0.45)",
                            color: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        <LeftOutlined />
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        style={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            border: "none",
                            background: "rgba(0,0,0,0.45)",
                            color: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        <RightOutlined />
                    </button>
                </>
            )}
        </>
    );

    const renderStoryChrome = () => (
        <>
            {media.length > 0 && (
                <div
                    style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        right: 8,
                        display: "flex",
                        gap: 4,
                    }}
                >
                    {media.map((_, i) => (
                        <div
                            key={i}
                            style={{
                                flex: 1,
                                height: 2,
                                background: "rgba(255,255,255,0.35)",
                                overflow: "hidden",
                                borderRadius: 2,
                            }}
                        >
                            <div
                                style={{
                                    width:
                                        i < index
                                            ? "100%"
                                            : i === index
                                                ? storyAutoAdvance
                                                    ? "100%"
                                                    : "0%"
                                                : "0%",
                                    height: "100%",
                                    background: "#fff",
                                    transition: storyAutoAdvance && i === index ? `width ${Math.max(200, storyAdvanceMs)}ms linear` : "none",
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {current?.type === "video" && (
                <button
                    type="button"
                    onClick={() => setIsStoryMuted((m) => !m)}
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 16,
                        padding: "2px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: "pointer",
                    }}
                >
                    {isStoryMuted ? <SoundOutlined /> : <SoundFilled />}
                    <span style={{ fontSize: 12 }}>{isStoryMuted ? "Muted" : "Sound"}</span>
                </button>
            )}

            {hasMultiple && (
                <>
                    <button
                        type="button"
                        onClick={onPrev}
                        aria-label="previous story"
                        style={{ position: "absolute", inset: 0, right: "50%", background: "transparent", border: "none" }}
                    />
                    <button
                        type="button"
                        onClick={onNext}
                        aria-label="next story"
                        style={{ position: "absolute", inset: 0, left: "50%", background: "transparent", border: "none" }}
                    />
                </>
            )}

            {(text || mode === "story") && (
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: "48px 12px 12px",
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.75) 100%)",
                        color: "#fff",
                    }}
                >
                </div>
            )}
        </>
    );

    const MediaFrame = ({ children }) => (
        <div
            style={{
                position: "relative",
                width: "100%",
                aspectRatio: mode === "story" ? "9/16" : "1/1",
                maxHeight: mode === "story" ? 680 : "auto",
                borderRadius: 12,
                overflow: "hidden",
                background: "#000",
            }}
        >
            {children}
        </div>
    );

    return (
        <Card size="small" bordered>
            {renderHeader()}

            <MediaFrame>
                {renderMedia()}
                {mode === "post" ? renderPostChrome() : renderStoryChrome()}
            </MediaFrame>

            {mode === "post" && (
                <>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: 10,
                        }}
                    >
                        <Space size={16}>
                            <HeartOutlined style={{ fontSize: 20 }} />
                            <MessageOutlined style={{ fontSize: 20 }} />
                            <SendOutlined style={{ fontSize: 20 }} />
                        </Space>
                        <SaveOutlined style={{ fontSize: 20 }} />
                    </div>

                    {hasMultiple && (
                        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 6 }}>
                            {media.map((_, i) => (
                                <span
                                    key={i}
                                    style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        background: i === index ? "#262626" : "#bfbfbf",
                                        display: "inline-block",
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {text && (
                        <div style={{ marginTop: 4 }}>
                            <Typography.Paragraph style={{ marginBottom: 4, lineHeight: 1.25 }}>
                                {expanded ? (
                                    <>
                                        <TextRenderer text={text} />
                                        <Typography.Link
                                            onClick={() => setExpanded(false)}
                                            style={{ display: "block", marginTop: 4, marginLeft: 0, padding: 0, lineHeight: "inherit" }}
                                        >
                                            {t("post.rollUp")}
                                        </Typography.Link>
                                    </>
                                ) : (
                                    <>
                                        <TextRenderer text={text} clampChars={clamp} />
                                        {text.length > clamp && (
                                            <Typography.Link
                                                onClick={() => setExpanded(true)}
                                                style={{ display: "block", marginTop: 4, marginLeft: 0, padding: 0, lineHeight: "inherit" }}
                                            >
                                                {t("post.showMore")}
                                            </Typography.Link>
                                        )}
                                    </>
                                )}
                            </Typography.Paragraph>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
